import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { DateUtils } from '@/utils/date-utils';
import { TTicket } from '../../../types/ticket.type';

interface ISpaceFilterState {
  titleSearch: string;
  creatorTickets: string[];
  createdAt: {
    startDate: string;
    endDate: string;
  };
  isCompleted: boolean;
  isNotCompleted: boolean;
  isNoMember: boolean;
  isNoLabelTicket: boolean;
  memberTickets: string[];
  labelTickets: string[];
  noDate: boolean;
  overDue: boolean;
  dueInNextDay: boolean;
  dueInNextDays: boolean;
  dueInNextMonth: boolean;
  dueInLastWeeks: boolean;
  dueInLastMonths: boolean;
  dueInTimeFrame: {
    startDate: string;
    endDate: string;
  };
}

const getDefaultFilterState = (): ISpaceFilterState => ({
  titleSearch: '',
  creatorTickets: [],
  createdAt: { startDate: '', endDate: '' },
  isCompleted: false,
  isNotCompleted: false,
  isNoMember: false,
  isNoLabelTicket: false,
  memberTickets: [],
  labelTickets: [],
  noDate: false,
  overDue: false,
  dueInNextDay: false,
  dueInNextDays: false,
  dueInNextMonth: false,
  dueInLastWeeks: false,
  dueInLastMonths: false,
  dueInTimeFrame: { startDate: '', endDate: '' },
});

export interface IFilterTicketModalStore {
  /**
   * Current Space ID - Filters are space-specific
   */
  currentSpaceId: string | null;

  /**
   * Filters stored per space
   */
  filtersBySpace: Record<string, ISpaceFilterState>;

  /**
   * State (Applied Filters) - Current space's filters
   */
  titleSearch: string;
  createdAt: {
    startDate: string;
    endDate: string;
  };
  isCompleted: boolean;
  isNotCompleted: boolean;
  isNoMember: boolean;
  isNoLabelTicket: boolean;
  creatorTickets: string[];
  memberTickets: string[];
  labelTickets: string[];
  noDate: boolean;
  overDue: boolean;
  dueInNextDay: boolean;
  dueInNextDays: boolean;
  dueInNextMonth: boolean;
  dueInLastWeeks: boolean;
  dueInLastMonths: boolean;
  dueInTimeFrame: {
    startDate: string;
    endDate: string;
  };

  /**
   * Pending State (Changes in Modal - Not Applied Yet)
   */
  pendingTitleSearch: string;
  pendingCreatedAt: {
    startDate: string;
    endDate: string;
  };
  pendingIsCompleted: boolean;
  pendingIsNotCompleted: boolean;
  pendingIsNoMember: boolean;
  pendingIsNoLabelTicket: boolean;
  pendingCreatorTickets: string[];
  pendingMemberTickets: string[];
  pendingLabelTickets: string[];
  pendingNoDate: boolean;
  pendingOverDue: boolean;
  pendingDueInNextDay: boolean;
  pendingDueInNextDays: boolean;
  pendingDueInNextMonth: boolean;
  pendingDueInLastWeeks: boolean;
  pendingDueInLastMonths: boolean;
  pendingDueInTimeFrame: {
    startDate: string;
    endDate: string;
  };

  /**
   * Actions
   */
  actions: {
    // Space management
    setCurrentSpaceId: (spaceId: string | null) => void;

    // Set applied filters
    setTitleSearch: (titleSearch: string) => void;
    setIsCompleted: (isCompleted: boolean) => void;
    setIsNotCompleted: (isNotCompleted: boolean) => void;
    setCreatedAt: (createdAt: { startDate: string; endDate: string }) => void;
    setIsNoMember: (isNoMember: boolean) => void;
    setIsNoLabelTicket: (isNoLabelTicket: boolean) => void;
    setCreatorTickets: (creatorTickets: string[]) => void;
    setMemberTickets: (memberTickets: string[]) => void;
    setLabelTickets: (labelTickets: string[]) => void;
    setNoDate: (noDate: boolean) => void;
    setOverDue: (overDue: boolean) => void;
    setDueInNextDay: (dueInNextDay: boolean) => void;
    setDueInNextDays: (dueInNextDays: boolean) => void;
    setDueInNextMonth: (dueInNextMonth: boolean) => void;
    setDueInLastWeeks: (dueInLastWeeks: boolean) => void;
    setDueInLastMonths: (dueInLastMonths: boolean) => void;
    setDueInTimeFrame: (dueInTimeFrame: { startDate: string; endDate: string }) => void;
    resetFilters: () => void;

    // Set pending filters
    setPendingTitleSearch: (titleSearch: string) => void;
    setPendingIsCompleted: (isCompleted: boolean) => void;
    setPendingIsNotCompleted: (isNotCompleted: boolean) => void;
    setPendingCreatedAt: (createdAt: { startDate: string; endDate: string }) => void;
    setPendingIsNoMember: (isNoMember: boolean) => void;
    setPendingIsNoLabelTicket: (isNoLabelTicket: boolean) => void;
    setPendingCreatorTickets: (creatorTickets: string[]) => void;
    setPendingMemberTickets: (memberTickets: string[]) => void;
    setPendingLabelTickets: (labelTickets: string[]) => void;
    setPendingNoDate: (noDate: boolean) => void;
    setPendingOverDue: (overDue: boolean) => void;
    setPendingDueInNextDay: (dueInNextDay: boolean) => void;
    setPendingDueInNextDays: (dueInNextDays: boolean) => void;
    setPendingDueInNextMonth: (dueInNextMonth: boolean) => void;
    setPendingDueInLastWeeks: (dueInLastWeeks: boolean) => void;
    setPendingDueInLastMonths: (dueInLastMonths: boolean) => void;
    setPendingDueInTimeFrame: (dueInTimeFrame: { startDate: string; endDate: string }) => void;
    resetPendingFilters: () => void;

    // Apply pending filters
    applyPendingFilters: () => void;

    // Check if there are pending changes
    hasPendingChanges: () => boolean;

    // Filter utilities
    checkTicketMatchesFilters: (ticket: TTicket) => boolean;
    filterTickets: (tickets: TTicket[]) => TTicket[];
  };
}

export const filterTicketModalStore = create<IFilterTicketModalStore>((set, get) => ({
  // Current space ID
  currentSpaceId: null,

  // Filters stored per space
  filtersBySpace: {},

  // Applied filters (current space's filters)
  titleSearch: '',
  createdAt: { startDate: '', endDate: '' },
  isCompleted: false,
  isNotCompleted: false,
  isNoMember: false,
  isNoLabelTicket: false,
  creatorTickets: [],
  memberTickets: [],
  labelTickets: [],
  noDate: false,
  overDue: false,
  dueInNextDay: false,
  dueInNextDays: false,
  dueInNextMonth: false,
  dueInLastWeeks: false,
  dueInLastMonths: false,
  dueInTimeFrame: { startDate: '', endDate: '' },

  // Pending filters
  pendingTitleSearch: '',
  pendingCreatedAt: { startDate: '', endDate: '' },
  pendingIsCompleted: false,
  pendingIsNotCompleted: false,
  pendingIsNoMember: false,
  pendingIsNoLabelTicket: false,
  pendingCreatorTickets: [],
  pendingMemberTickets: [],
  pendingLabelTickets: [],
  pendingNoDate: false,
  pendingOverDue: false,
  pendingDueInNextDay: false,
  pendingDueInNextDays: false,
  pendingDueInNextMonth: false,
  pendingDueInLastWeeks: false,
  pendingDueInLastMonths: false,
  pendingDueInTimeFrame: { startDate: '', endDate: '' },

  /**
   * Actions
   */
  actions: {
    /**
     * Set the current space ID and load filters for that space
     */
    setCurrentSpaceId: (spaceId: string | null) => {
      const state = get();

      // Save current space's filters before switching
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId && state.currentSpaceId !== spaceId) {
        const currentFilters: ISpaceFilterState = {
          titleSearch: state.titleSearch,
          creatorTickets: state.creatorTickets,
          createdAt: state.createdAt,
          isCompleted: state.isCompleted,
          isNotCompleted: state.isNotCompleted,
          isNoMember: state.isNoMember,
          isNoLabelTicket: state.isNoLabelTicket,
          memberTickets: state.memberTickets,
          labelTickets: state.labelTickets,
          noDate: state.noDate,
          overDue: state.overDue,
          dueInNextDay: state.dueInNextDay,
          dueInNextDays: state.dueInNextDays,
          dueInNextMonth: state.dueInNextMonth,
          dueInLastWeeks: state.dueInLastWeeks,
          dueInLastMonths: state.dueInLastMonths,
          dueInTimeFrame: state.dueInTimeFrame,
        };

        updatedFiltersBySpace[state.currentSpaceId] = currentFilters;
      }

      // Load filters for the new space (or use defaults if none exist)
      if (spaceId) {
        const spaceFilters = updatedFiltersBySpace[spaceId] || getDefaultFilterState();
        set({
          currentSpaceId: spaceId,
          // Load applied filters for the new space
          titleSearch: spaceFilters.titleSearch,
          createdAt: spaceFilters.createdAt,
          isCompleted: spaceFilters.isCompleted,
          isNotCompleted: spaceFilters.isNotCompleted,
          isNoMember: spaceFilters.isNoMember,
          isNoLabelTicket: spaceFilters.isNoLabelTicket,
          creatorTickets: spaceFilters.creatorTickets,
          memberTickets: spaceFilters.memberTickets,
          labelTickets: spaceFilters.labelTickets,
          noDate: spaceFilters.noDate,
          overDue: spaceFilters.overDue,
          dueInNextDay: spaceFilters.dueInNextDay,
          dueInNextDays: spaceFilters.dueInNextDays,
          dueInNextMonth: spaceFilters.dueInNextMonth,
          dueInLastWeeks: spaceFilters.dueInLastWeeks,
          dueInLastMonths: spaceFilters.dueInLastMonths,
          dueInTimeFrame: spaceFilters.dueInTimeFrame,

          // Reset pending filters to match applied filters
          pendingTitleSearch: spaceFilters.titleSearch,
          pendingCreatedAt: spaceFilters.createdAt,
          pendingIsCompleted: spaceFilters.isCompleted,
          pendingIsNotCompleted: spaceFilters.isNotCompleted,
          pendingIsNoMember: spaceFilters.isNoMember,
          pendingIsNoLabelTicket: spaceFilters.isNoLabelTicket,
          pendingCreatorTickets: spaceFilters.creatorTickets,
          pendingMemberTickets: spaceFilters.memberTickets,
          pendingLabelTickets: spaceFilters.labelTickets,
          pendingNoDate: spaceFilters.noDate,
          pendingOverDue: spaceFilters.overDue,
          pendingDueInNextDay: spaceFilters.dueInNextDay,
          pendingDueInNextDays: spaceFilters.dueInNextDays,
          pendingDueInNextMonth: spaceFilters.dueInNextMonth,
          pendingDueInLastWeeks: spaceFilters.dueInLastWeeks,
          pendingDueInLastMonths: spaceFilters.dueInLastMonths,
          pendingDueInTimeFrame: spaceFilters.dueInTimeFrame,
          // Update filtersBySpace with saved filters
          filtersBySpace: updatedFiltersBySpace,
        });
      } else {
        // No space selected, reset to defaults
        const defaultFilters = getDefaultFilterState();
        set({
          currentSpaceId: null,
          ...defaultFilters,
          pendingCreatedAt: defaultFilters.createdAt,
          pendingIsCompleted: defaultFilters.isCompleted,
          pendingIsNotCompleted: defaultFilters.isNotCompleted,
          pendingIsNoMember: defaultFilters.isNoMember,
          pendingIsNoLabelTicket: defaultFilters.isNoLabelTicket,
          pendingCreatorTickets: defaultFilters.creatorTickets,
          pendingMemberTickets: defaultFilters.memberTickets,
          pendingLabelTickets: defaultFilters.labelTickets,
          pendingNoDate: defaultFilters.noDate,
          pendingOverDue: defaultFilters.overDue,
          pendingDueInNextDay: defaultFilters.dueInNextDay,
          pendingDueInNextDays: defaultFilters.dueInNextDays,
          pendingDueInNextMonth: defaultFilters.dueInNextMonth,
          pendingDueInLastWeeks: defaultFilters.dueInLastWeeks,
          pendingDueInLastMonths: defaultFilters.dueInLastMonths,
          pendingDueInTimeFrame: defaultFilters.dueInTimeFrame,
          filtersBySpace: updatedFiltersBySpace,
        });
      }
    },

    // Set applied filters (also save to current space)
    setTitleSearch: (titleSearch: string) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          titleSearch,
        };
      }
      set({ titleSearch, filtersBySpace: updatedFiltersBySpace });
    },
    setCreatedAt: (createdAt: { startDate: string; endDate: string }) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          createdAt,
        };
      }
      set({ createdAt, filtersBySpace: updatedFiltersBySpace });
    },
    setIsCompleted: (isCompleted: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          isCompleted,
        };
      }
      set({ isCompleted, filtersBySpace: updatedFiltersBySpace });
    },
    setIsNotCompleted: (isNotCompleted: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          isNotCompleted,
        };
      }
      set({ isNotCompleted, filtersBySpace: updatedFiltersBySpace });
    },
    setIsNoMember: (isNoMember: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          isNoMember,
        };
      }
      set({ isNoMember, filtersBySpace: updatedFiltersBySpace });
    },
    setIsNoLabelTicket: (isNoLabelTicket: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          isNoLabelTicket,
        };
      }
      set({ isNoLabelTicket, filtersBySpace: updatedFiltersBySpace });
    },
    setCreatorTickets: (creatorTickets: string[]) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          creatorTickets,
        };
      }
      set({ creatorTickets, filtersBySpace: updatedFiltersBySpace });
    },
    setMemberTickets: (memberTickets: string[]) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          memberTickets,
        };
      }
      set({ memberTickets, filtersBySpace: updatedFiltersBySpace });
    },
    setLabelTickets: (labelTickets: string[]) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          labelTickets,
        };
      }
      set({ labelTickets, filtersBySpace: updatedFiltersBySpace });
    },
    setNoDate: (noDate: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          noDate,
        };
      }
      set({ noDate, filtersBySpace: updatedFiltersBySpace });
    },
    setOverDue: (overDue: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          overDue,
        };
      }
      set({ overDue, filtersBySpace: updatedFiltersBySpace });
    },
    setDueInNextDay: (dueInNextDay: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          dueInNextDay,
        };
      }
      set({ dueInNextDay, filtersBySpace: updatedFiltersBySpace });
    },
    setDueInNextDays: (dueInNextDays: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          dueInNextDays,
        };
      }
      set({ dueInNextDays, filtersBySpace: updatedFiltersBySpace });
    },
    setDueInNextMonth: (dueInNextMonth: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          dueInNextMonth,
        };
      }
      set({ dueInNextMonth, filtersBySpace: updatedFiltersBySpace });
    },
    setDueInLastWeeks: (dueInLastWeeks: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          dueInLastWeeks,
        };
      }
      set({ dueInLastWeeks, filtersBySpace: updatedFiltersBySpace });
    },
    setDueInLastMonths: (dueInLastMonths: boolean) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          dueInLastMonths,
        };
      }
      set({ dueInLastMonths, filtersBySpace: updatedFiltersBySpace });
    },
    setDueInTimeFrame: (dueInTimeFrame: { startDate: string; endDate: string }) => {
      const state = get();
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = {
          ...(updatedFiltersBySpace[state.currentSpaceId] || getDefaultFilterState()),
          dueInTimeFrame,
        };
      }
      set({ dueInTimeFrame, filtersBySpace: updatedFiltersBySpace });
    },
    resetFilters: () => {
      const state = get();
      const defaultFilters = getDefaultFilterState();

      // Reset current space's filters
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = defaultFilters;
      }

      set({
        ...defaultFilters,
        pendingTitleSearch: defaultFilters.titleSearch,
        pendingCreatedAt: defaultFilters.createdAt,
        pendingIsCompleted: defaultFilters.isCompleted,
        pendingIsNotCompleted: defaultFilters.isNotCompleted,
        pendingIsNoMember: defaultFilters.isNoMember,
        pendingIsNoLabelTicket: defaultFilters.isNoLabelTicket,
        pendingCreatorTickets: defaultFilters.creatorTickets,
        pendingMemberTickets: defaultFilters.memberTickets,
        pendingLabelTickets: defaultFilters.labelTickets,
        pendingNoDate: defaultFilters.noDate,
        pendingOverDue: defaultFilters.overDue,
        pendingDueInNextDay: defaultFilters.dueInNextDay,
        pendingDueInNextDays: defaultFilters.dueInNextDays,
        pendingDueInNextMonth: defaultFilters.dueInNextMonth,
        pendingDueInLastWeeks: defaultFilters.dueInLastWeeks,
        pendingDueInLastMonths: defaultFilters.dueInLastMonths,
        pendingDueInTimeFrame: defaultFilters.dueInTimeFrame,
        filtersBySpace: updatedFiltersBySpace,
      });
    },

    // Set pending filters
    setPendingTitleSearch: (titleSearch: string) => {
      set({ pendingTitleSearch: titleSearch });
    },
    setPendingCreatedAt: (createdAt: { startDate: string; endDate: string }) => {
      set({ pendingCreatedAt: createdAt });
    },
    setPendingIsCompleted: (isCompleted: boolean) => {
      set({ pendingIsCompleted: isCompleted });
    },
    setPendingIsNotCompleted: (isNotCompleted: boolean) => {
      set({ pendingIsNotCompleted: isNotCompleted });
    },
    setPendingIsNoMember: (isNoMember: boolean) => {
      set({ pendingIsNoMember: isNoMember });
    },
    setPendingIsNoLabelTicket: (isNoLabelTicket: boolean) => {
      set({ pendingIsNoLabelTicket: isNoLabelTicket });
    },
    setPendingCreatorTickets: (creatorTickets: string[]) => {
      set({ pendingCreatorTickets: creatorTickets });
    },
    setPendingMemberTickets: (memberTickets: string[]) => {
      set({ pendingMemberTickets: memberTickets });
    },
    setPendingLabelTickets: (labelTickets: string[]) => {
      set({ pendingLabelTickets: labelTickets });
    },
    setPendingNoDate: (noDate: boolean) => {
      set({ pendingNoDate: noDate });
    },
    setPendingOverDue: (overDue: boolean) => {
      set({ pendingOverDue: overDue });
    },
    setPendingDueInNextDay: (dueInNextDay: boolean) => {
      set({ pendingDueInNextDay: dueInNextDay });
    },
    setPendingDueInNextDays: (dueInNextDays: boolean) => {
      set({ pendingDueInNextDays: dueInNextDays });
    },
    setPendingDueInNextMonth: (dueInNextMonth: boolean) => {
      set({ pendingDueInNextMonth: dueInNextMonth });
    },
    setPendingDueInLastWeeks: (dueInLastWeeks: boolean) => {
      set({ pendingDueInLastWeeks: dueInLastWeeks });
    },
    setPendingDueInLastMonths: (dueInLastMonths: boolean) => {
      set({ pendingDueInLastMonths: dueInLastMonths });
    },
    setPendingDueInTimeFrame: (dueInTimeFrame: { startDate: string; endDate: string }) => {
      set({ pendingDueInTimeFrame: dueInTimeFrame });
    },
    resetPendingFilters: () => {
      const state = get();
      set({
        pendingTitleSearch: state.titleSearch,
        pendingCreatedAt: state.createdAt,
        pendingIsCompleted: state.isCompleted,
        pendingIsNotCompleted: state.isNotCompleted,
        pendingIsNoMember: state.isNoMember,
        pendingIsNoLabelTicket: state.isNoLabelTicket,
        pendingCreatorTickets: state.creatorTickets,
        pendingMemberTickets: state.memberTickets,
        pendingLabelTickets: state.labelTickets,
        pendingNoDate: state.noDate,
        pendingOverDue: state.overDue,
        pendingDueInNextDay: state.dueInNextDay,
        pendingDueInNextDays: state.dueInNextDays,
        pendingDueInNextMonth: state.dueInNextMonth,
        pendingDueInLastWeeks: state.dueInLastWeeks,
        pendingDueInLastMonths: state.dueInLastMonths,
        pendingDueInTimeFrame: state.dueInTimeFrame,
      });
    },

    /**
     * Apply pending filters to current filters
     * This should be called when the "Filter" button is clicked
     */
    applyPendingFilters: () => {
      const state = get();
      const appliedFilters: ISpaceFilterState = {
        titleSearch: state.pendingTitleSearch,
        creatorTickets: state.pendingCreatorTickets,
        createdAt: state.pendingCreatedAt,
        isCompleted: state.pendingIsCompleted,
        isNotCompleted: state.pendingIsNotCompleted,
        isNoMember: state.pendingIsNoMember,
        isNoLabelTicket: state.pendingIsNoLabelTicket,
        memberTickets: state.pendingMemberTickets,
        labelTickets: state.pendingLabelTickets,
        noDate: state.pendingNoDate,
        overDue: state.pendingOverDue,
        dueInNextDay: state.pendingDueInNextDay,
        dueInNextDays: state.pendingDueInNextDays,
        dueInNextMonth: state.pendingDueInNextMonth,
        dueInLastWeeks: state.pendingDueInLastWeeks,
        dueInLastMonths: state.pendingDueInLastMonths,
        dueInTimeFrame: state.pendingDueInTimeFrame,
      };

      // Save to current space's filters
      const updatedFiltersBySpace = { ...state.filtersBySpace };
      if (state.currentSpaceId) {
        updatedFiltersBySpace[state.currentSpaceId] = appliedFilters;
      }

      set({
        titleSearch: appliedFilters.titleSearch,
        createdAt: appliedFilters.createdAt,
        isCompleted: appliedFilters.isCompleted,
        isNotCompleted: appliedFilters.isNotCompleted,
        isNoMember: appliedFilters.isNoMember,
        isNoLabelTicket: appliedFilters.isNoLabelTicket,
        creatorTickets: appliedFilters.creatorTickets,
        memberTickets: appliedFilters.memberTickets,
        labelTickets: appliedFilters.labelTickets,
        noDate: appliedFilters.noDate,
        overDue: appliedFilters.overDue,
        dueInNextDay: appliedFilters.dueInNextDay,
        dueInNextDays: appliedFilters.dueInNextDays,
        dueInNextMonth: appliedFilters.dueInNextMonth,
        dueInLastWeeks: appliedFilters.dueInLastWeeks,
        dueInLastMonths: appliedFilters.dueInLastMonths,
        dueInTimeFrame: appliedFilters.dueInTimeFrame,
        filtersBySpace: updatedFiltersBySpace,
      });
    },

    /**
     * Check if there are pending changes that haven't been applied
     */
    hasPendingChanges: () => {
      const state = get();
      return (
        state.pendingTitleSearch !== state.titleSearch ||
        JSON.stringify(state.pendingCreatedAt) !== JSON.stringify(state.createdAt) ||
        state.pendingIsCompleted !== state.isCompleted ||
        state.pendingIsNotCompleted !== state.isNotCompleted ||
        state.pendingIsNoMember !== state.isNoMember ||
        state.pendingIsNoLabelTicket !== state.isNoLabelTicket ||
        JSON.stringify(state.pendingCreatorTickets) !== JSON.stringify(state.creatorTickets) ||
        JSON.stringify(state.pendingMemberTickets) !== JSON.stringify(state.memberTickets) ||
        JSON.stringify(state.pendingLabelTickets) !== JSON.stringify(state.labelTickets) ||
        state.pendingNoDate !== state.noDate ||
        state.pendingOverDue !== state.overDue ||
        state.pendingDueInNextDay !== state.dueInNextDay ||
        state.pendingDueInNextDays !== state.dueInNextDays ||
        state.pendingDueInNextMonth !== state.dueInNextMonth ||
        state.pendingDueInLastWeeks !== state.dueInLastWeeks ||
        state.pendingDueInLastMonths !== state.dueInLastMonths ||
        JSON.stringify(state.pendingDueInTimeFrame) !== JSON.stringify(state.dueInTimeFrame)
      );
    },
    /**
     * Check if a ticket matches the current filter conditions using date-fns utilities
     */
    checkTicketMatchesFilters: (ticket: TTicket): boolean => {
      const state = get();
      const { endDate, isCompleted, createdAt, title } = ticket;

      // Check title search filter (case-insensitive substring match)
      const titleSearchTrimmed = state.titleSearch?.trim() || '';
      if (titleSearchTrimmed) {
        const ticketTitle = (title ?? '').toLowerCase();
        const searchTerm = titleSearchTrimmed.toLowerCase();
        if (!ticketTitle.includes(searchTerm)) {
          return false;
        }
      }

      // Check completion status filters
      if (state.isCompleted && !isCompleted) {
        return false;
      }

      if (state.isNotCompleted && isCompleted) {
        return false;
      }

      // Check createdAt filter
      if (state.createdAt.startDate && state.createdAt.endDate) {
        if (!DateUtils.isDueInTimeframe(createdAt, state.createdAt.startDate, state.createdAt.endDate)) {
          return false;
        }
      }

      // Check member filter
      if (state.isNoMember) {
        if (!ticket.users || ticket.users.length > 0) {
          return false;
        }
      }

      if (state.memberTickets.length > 0) {
        const ticketUserIds = ticket.users?.map((user) => user.userId) || [];
        const hasMatchingMember = state.memberTickets.some((memberId) => ticketUserIds.includes(memberId));
        if (!hasMatchingMember) {
          return false;
        }
      }

      // Check label filter
      if (state.isNoLabelTicket) {
        if (!ticket.ticketLabels || ticket.ticketLabels.length > 0) {
          return false;
        }
      }

      if (state.labelTickets.length > 0) {
        const ticketLabelIds = ticket.ticketLabels?.map((label) => label.labelTicketId) || [];
        const hasMatchingLabel = state.labelTickets.some((labelId) => ticketLabelIds.includes(labelId));
        if (!hasMatchingLabel) {
          return false;
        }
      }

      // Check creator filter
      if (state.creatorTickets.length > 0) {
        const ticketCreatorId = ticket.creator?.userId || null;
        const hasMatchingCreator = ticketCreatorId && state.creatorTickets.includes(ticketCreatorId);
        if (!hasMatchingCreator) {
          return false;
        }
      }

      // Check date filters using DateUtils from date-utils.ts
      const dateFilters = [
        { enabled: state.noDate, check: () => DateUtils.isNoDate(endDate) },
        { enabled: state.overDue, check: () => DateUtils.isOverDue(endDate) },
        { enabled: state.dueInNextDay, check: () => DateUtils.isDueInNextDays(endDate, 1) },
        { enabled: state.dueInNextDays, check: () => DateUtils.isDueInNextDays(endDate, 7) },
        { enabled: state.dueInNextMonth, check: () => DateUtils.isDueInNextDays(endDate, 30) },
        { enabled: state.dueInLastWeeks, check: () => DateUtils.isDueInLastWeeks(endDate) },
        { enabled: state.dueInLastMonths, check: () => DateUtils.isDueInLastMonths(endDate) },
        {
          enabled: !!(state.dueInTimeFrame.startDate && state.dueInTimeFrame.endDate),
          check: () => {
            if (!state.dueInTimeFrame.startDate || !state.dueInTimeFrame.endDate) return false;
            return DateUtils.isDueInTimeframe(endDate, state.dueInTimeFrame.startDate, state.dueInTimeFrame.endDate);
          },
        },
      ];

      // If any date filter is enabled, the ticket must match at least one
      const enabledDateFilters = dateFilters.filter((filter) => filter.enabled);
      if (enabledDateFilters.length > 0) {
        const matchesAnyDateFilter = enabledDateFilters.some((filter) => filter.check());
        if (!matchesAnyDateFilter) {
          return false;
        }
      }

      return true;
    },
    /**
     * Filter an array of tickets based on current filter conditions using date-fns utilities
     */
    filterTickets: (tickets: TTicket[]): TTicket[] => {
      const { checkTicketMatchesFilters } = get().actions;
      return tickets.filter(checkTicketMatchesFilters);
    },
  },
}));

// Selectors
export const useFilterTicketModalActions = () => filterTicketModalStore(useShallow((state) => state.actions));

export const useFilterTicketModalState = () =>
  filterTicketModalStore(
    useShallow((state) => ({
      titleSearch: state.titleSearch,
      createdAt: state.createdAt,
      isCompleted: state.isCompleted,
      isNotCompleted: state.isNotCompleted,
      isNoMember: state.isNoMember,
      isNoLabelTicket: state.isNoLabelTicket,
      creatorTickets: state.creatorTickets,
      memberTickets: state.memberTickets,
      labelTickets: state.labelTickets,
      noDate: state.noDate,
      overDue: state.overDue,
      dueInNextDay: state.dueInNextDay,
      dueInNextDays: state.dueInNextDays,
      dueInNextMonth: state.dueInNextMonth,
      dueInLastWeeks: state.dueInLastWeeks,
      dueInLastMonths: state.dueInLastMonths,
      dueInTimeFrame: state.dueInTimeFrame,
    }))
  );

export const useFilterTicketModalPendingState = () =>
  filterTicketModalStore(
    useShallow((state) => ({
      pendingTitleSearch: state.pendingTitleSearch,
      pendingCreatedAt: state.pendingCreatedAt,
      pendingIsCompleted: state.pendingIsCompleted,
      pendingIsNotCompleted: state.pendingIsNotCompleted,
      pendingIsNoMember: state.pendingIsNoMember,
      pendingIsNoLabelTicket: state.pendingIsNoLabelTicket,
      pendingCreatorTickets: state.pendingCreatorTickets,
      pendingMemberTickets: state.pendingMemberTickets,
      pendingLabelTickets: state.pendingLabelTickets,
      pendingNoDate: state.pendingNoDate,
      pendingOverDue: state.pendingOverDue,
      pendingDueInNextDay: state.pendingDueInNextDay,
      pendingDueInNextDays: state.pendingDueInNextDays,
      pendingDueInNextMonth: state.pendingDueInNextMonth,
      pendingDueInLastWeeks: state.pendingDueInLastWeeks,
      pendingDueInLastMonths: state.pendingDueInLastMonths,
      pendingDueInTimeFrame: state.pendingDueInTimeFrame,
    }))
  );

/**
 * Hook to get the count of active filters
 * Counts how many filter conditions are currently applied
 */
export const useFilterTicketModalActiveCount = () =>
  filterTicketModalStore(
    useShallow((state) => {
      let count = 0;

      // Count title search filter
      if (state.titleSearch?.trim()) count++;

      // Count status filters
      if (state.isCompleted) count++;
      if (state.isNotCompleted) count++;

      // Count createdAt filter
      if (state.createdAt.startDate && state.createdAt.endDate) count++;

      // Count member filters
      if (state.isNoMember) count++;
      if (state.memberTickets.length > 0) count++;

      // Count label filters
      if (state.isNoLabelTicket) count++;
      if (state.labelTickets.length > 0) count++;

      // Count creator filters
      if (state.creatorTickets.length > 0) count++;

      // Count date filters
      if (state.noDate) count++;
      if (state.overDue) count++;
      if (state.dueInNextDay) count++;
      if (state.dueInNextDays) count++;
      if (state.dueInNextMonth) count++;
      if (state.dueInLastWeeks) count++;
      if (state.dueInLastMonths) count++;
      if (state.dueInTimeFrame.startDate && state.dueInTimeFrame.endDate) count++;

      return count;
    })
  );
